import React, { createRef, useEffect, useState } from "react";
import { Button, Stack, Table } from "react-bootstrap";

// hack to allow non-standard attributes
declare module "react" {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    // extends React's HTMLAttributes
    directory?: string;
    webkitdirectory?: string;
  }
}
// end hack

interface MultiFileUploadProps {
  directories?: boolean;
  multiple?: boolean;
  dragAndDropEnabled: boolean;
  filter?: string;
  onCancel?: () => void;
  onUploadFile?: (name: string, file: File) => Promise<boolean>;
  onStarted?: () => void;
  onFinished?: () => void;
}

interface SingleFile {
  file: File;
  fullPath: string;
}

interface Entry {
  name: string;
  files: SingleFile[];
}

function MultiFileUpload({
  directories = false,
  multiple = false,
  dragAndDropEnabled,
  filter,
  onCancel,
  onUploadFile,
  onStarted,
  onFinished,
}: MultiFileUploadProps) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [uploadQueue, setUploadQueue] = useState<SingleFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    async function uploadNextFile() {
      if (!onUploadFile || uploadQueue.length === 0) return;

      let nextFile = uploadQueue[0];
      let name =
        nextFile.fullPath && nextFile.fullPath.length > 0
          ? nextFile.fullPath
          : nextFile.file.name;

      let result = await onUploadFile(name, nextFile.file);

      if (result) {
        // also remove file from list
        setEntries(
          entries
            .map((entry) => {
              const fileIndex = entry.files.findIndex((f) => f === nextFile);

              if (fileIndex < 0) return entry;

              return {
                name: entry.name,
                files: entry.files.filter((_f, index) => index !== fileIndex),
              };
            })
            .filter((entry) => entry.files.length !== 0),
        );

        setUploadQueue(uploadQueue.slice(1));
      } else {
        setIsUploading(false);
      }
    }

    if (isUploading && uploadQueue.length > 0) {
      uploadNextFile();
    } else if (isUploading) {
      onFinished && onFinished();
      setIsUploading(false);
    }
  }, [uploadQueue, onUploadFile, entries, isUploading, onFinished]);

  const refFileInput = createRef<HTMLInputElement>();
  const refDirectoryInput = createRef<HTMLInputElement>();

  const uploadFiles = () => {
    if (!onUploadFile) return;

    onStarted?.();

    if (entries.length > 0 && entries[0].files.length > 0) {
      setIsUploading(true);
      setUploadQueue(entries.flatMap((entry) => entry.files));
    }
  };

  const addFiles = (files: SingleFile[]) => {
    let newFileList = [...entries];

    for (let ii = 0; ii < files.length; ++ii) {
      let f = files[ii];
      let entryToAdd: Entry = { name: "", files: [] };

      let indexOfPathSeparator = f.fullPath.indexOf("/");

      if (indexOfPathSeparator > 0) {
        let name = f.fullPath.substring(0, indexOfPathSeparator);

        if (name.length > 0) {
          // it's a path
          entryToAdd = { name: name, files: [f] };
        } else {
          // there's a path separator in the beginning? hmmm
        }
      } else {
        // isolated file
        entryToAdd = { name: f.file.name, files: [f] };
      }

      if (entryToAdd.name.length > 0 && entryToAdd.files.length > 0) {
        // update the list by adding new items and overwriting the ones that are already there
        const currentEntry = newFileList.find(
          (x) => x.name === entryToAdd.name,
        );

        if (currentEntry) {
          const currentFileIndex = currentEntry.files.findIndex(
            (x) => x.fullPath === entryToAdd.files[0].fullPath,
          );

          if (currentFileIndex >= 0) {
            currentEntry.files[currentFileIndex] = entryToAdd.files[0];
          } else {
            currentEntry.files.push(entryToAdd.files[0]);
          }
        } else {
          newFileList.push(entryToAdd);
        }
      }
    }
    setEntries(newFileList);
  };

  const inputFileChanged: React.ChangeEventHandler<HTMLInputElement> = (ev) => {
    if (ev.target.files && ev.target.files.length > 0) {
      const files = Array.from(ev.target.files);

      addFiles(
        files.map((f) => {
          return { file: f, fullPath: f.webkitRelativePath };
        }),
      );
    }
  };

  const dropHandlerAsync = async (
    e: React.DragEvent<HTMLElement>,
  ): Promise<boolean> => {
    e.preventDefault();

    if (!e.dataTransfer.items) {
      return true;
    }

    if (!multiple && entries.length >= 1) return true;

    let filesToAdd: SingleFile[] = [];
    let loadedEntries: FileSystemEntry[] = [];

    // e.dataTransfer.items clears up on edge on the second loop for some reason so we get the objects we need first
    for (let ii = 0; ii < e.dataTransfer.items.length; ++ii) {
      let entry = e.dataTransfer.items[ii].webkitGetAsEntry();
      if (entry) loadedEntries.push(entry);
    }

    for (let ii = 0; ii < loadedEntries.length; ++ii) {
      const entry = loadedEntries[ii];

      if (entry?.isDirectory) {
        if (!directories) continue;

        const directory = entry as FileSystemDirectoryEntry;

        let reader = directory.createReader();
        let fileEntries = await getFileEntriesAsync(reader);

        for (let jj = 0; jj < fileEntries.length; ++jj) {
          let f = await getFileFromFileEntryAsync(fileEntries[jj]);

          if (f) {
            let fullPath = f.webkitRelativePath ?? "";

            if (fullPath === "")
              fullPath = fileEntries[jj].fullPath.startsWith("/")
                ? fileEntries[jj].fullPath.substring(1)
                : fileEntries[jj].fullPath;
            filesToAdd.push({ file: f, fullPath: fullPath });
          }
        }
      } else if (entry?.isFile) {
        let f = await getFileFromFileEntryAsync(entry as FileSystemFileEntry);

        if (f) filesToAdd.push({ file: f, fullPath: f.webkitRelativePath });
      }
    }

    if (!multiple && filesToAdd.length > 1) {
      filesToAdd = [filesToAdd[0]];
    }

    addFiles(filesToAdd);

    return true;
  };

  const removeEntry = (entry: Entry) => {
    const newEntries = entries.filter((e) => e !== entry);
    setEntries(newEntries);
  };

  return (
    <>
      <div
        onDrop={(e) =>
          dragAndDropEnabled ? dropHandlerAsync(e) : e.preventDefault()
        }
        onDragOver={(e) => e.preventDefault()}
        style={{ height: "250px", overflowY: "auto" }}
      >
        {entries.length === 0 && (
          <>
            {multiple
              ? "Use the buttons below to add files to upload."
              : "Use the buttons below to add a file."}
          </>
        )}

        {entries.length > 0 && (
          <Table size="sm">
            <thead>
              <tr>
                <th colSpan={3} style={{ position: "sticky", top: 0 }}>
                  Filename
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((x, index) => (
                <tr key={index}>
                  <td>{x.name}</td>
                  <td
                    style={{
                      width: "1px",
                      whiteSpace: "nowrap",
                      textAlign: "right",
                    }}
                  >
                    {x.files.length > 1 && `${x.files.length} files`}
                  </td>
                  {isUploading ? (
                    <td style={{ width: "25px" }}></td>
                  ) : (
                    <td
                      style={{ width: "25px", cursor: "pointer" }}
                      onClick={() => removeEntry(x)}
                    >
                      x
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Stack direction="horizontal" gap={3} className="mt-3">
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => refFileInput.current?.click()}
          disabled={isUploading || (!multiple && entries.length > 0)}
        >
          {multiple ? "Add files" : "Add file"}
        </Button>
        {directories && (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => refDirectoryInput.current?.click()}
            disabled={isUploading}
          >
            Add directories
          </Button>
        )}
        <div className="me-auto"></div>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => {
            //xxx abort!
            setEntries([]);
            onCancel && onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => uploadFiles()}
          disabled={entries.length <= 0 || !onUploadFile || isUploading}
        >
          Upload all files
        </Button>
      </Stack>
      <input
        style={{ display: "none" }}
        ref={refFileInput}
        type="file"
        onChange={inputFileChanged}
        multiple={multiple}
        accept={filter}
      ></input>
      <input
        style={{ display: "none" }}
        ref={refDirectoryInput}
        type="file"
        onChange={inputFileChanged}
        multiple
        directory="true"
        webkitdirectory="true"
      ></input>
    </>
  );
}

// utility methods

// wraps the callbacks for FileSystemFileEntry.file() with a promise
function getFileFromFileEntryAsync(
  fileSystemEntry: FileSystemFileEntry,
): Promise<File | undefined> {
  return new Promise((resolve, reject) => {
    fileSystemEntry.file(resolve, reject);
  });
}

// wraps the callbacks for FileSystemDirectoryReader.readEntries() with a promise
async function get100EntriesFromDirectoryReaderAsync(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return await new Promise((resolve, reject) => {
    // this only returns max 100 entries on webkit
    reader.readEntries(resolve, reject);
  });
}

// ensures we get more than 100 entries (webkit limitation)
async function getEntriesFromDirectoryReaderAsync(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  let entries: FileSystemEntry[] = [];
  let readEntries = await get100EntriesFromDirectoryReaderAsync(reader);

  while (readEntries.length > 0) {
    entries.push(...readEntries);
    readEntries = await get100EntriesFromDirectoryReaderAsync(reader);
  }
  return entries;
}

// get all files from a directory reader *recursively*
async function getFileEntriesAsync(
  r: FileSystemDirectoryReader,
): Promise<FileSystemFileEntry[]> {
  let traversed: FileSystemFileEntry[] = [];

  const entries = await getEntriesFromDirectoryReaderAsync(r);

  for (let ii = 0; ii < entries.length; ++ii) {
    let e = entries[ii];
    if (e.isFile) traversed.push(e as FileSystemFileEntry);
    else if (e.isDirectory) {
      let directory = e as FileSystemDirectoryEntry;
      traversed = traversed.concat(
        await getFileEntriesAsync(directory.createReader()),
      );
    }
  }

  return traversed;
}

export default MultiFileUpload;
